import { useCallback, useEffect, useState } from "react";
import Select, { ActionMeta, InputActionMeta } from "react-select";
import { useNavigate } from "react-router-dom";
import Icon from "../Icon/Icon";
import { IconColorE, LangE } from "../../models/enums";
import { useTranslate } from "../../utils/hooks/useTranslate";
import useQueryParams from "../../utils/hooks/useQueryParams";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentLang } from "../../store/selectors/appSelectors";
import useCategory from "../AutocompleteSearch/hooks/useCategory";
import useAutocomplete from "../AutocompleteSearch/hooks/useAutocomplete";
import setLastValue from "../Search/helpers/setLastValue";
import getLastValue from "../Search/helpers/getLastValue";
import MenuList from "../Form/components/SelectField/components/MenuList";
import DropdownIndicator from "../AutocompleteSearch/components/DropdownIndicator/DropdownIndicator";

import type { SelectOptionT } from "../../models/models";

import styles from "./FloatingSearchForm.module.scss";

const FILTER_LABEL_QUERY_PARAM_NAME = "filterLabel";
const FILTER_ID_QUERY_PARAM_NAME = "filterId";
const FILTER_TYPE_QUERY_PARAM_NAME = "filterType";

const productionLocationOptions: SelectOptionT[] = [
  { value: "local", label: "Local" },
  { value: "abroad", label: "Abroad" },
];

type FloatingSearchFormProps = {
  isForCampaignsPage?: boolean;
  refetch?: () => void;
  withoutRedirect?: boolean;
  onSearchComplete?: () => void;
};

const MENU_Z_INDEX = 99999;

const categorySelectStyles = (lang: LangE) => ({
  control: (provided: object) => ({
    ...provided,
    height: 48,
    textAlign: "center",
    background: "#34A7F0",
    borderRadius: 25,
    border: "1px solid #E4E6E7",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { borderColor: "#E4E6E7", backgroundColor: "#248ACB" },
  }),
  placeholder: (provided: object) => ({
    ...provided,
    color: "#fff",
    fontFamily: lang === LangE.EN ? "Roboto" : "Rubik",
    fontSize: "0.87rem",
  }),
  valueContainer: (provided: object) => ({
    ...provided,
    padding: "0 20px",
    fontFamily: lang === LangE.EN ? "Roboto" : "Rubik",
    fontSize: "0.87rem",
  }),
  singleValue: (provided: object) => ({ ...provided, color: "#fff" }),
  indicatorSeparator: () => ({}),
  dropdownIndicator: (provided: object) => ({
    ...provided,
    padding: 8,
    paddingInlineEnd: 20,
  }),
  menu: (provided: object) => ({
    ...provided,
    zIndex: MENU_Z_INDEX,
  }),
  menuPortal: (provided: object) => ({
    ...provided,
    zIndex: MENU_Z_INDEX,
  }),
  option: (provided: object, state: { isSelected: boolean }) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#333" : "#fff",
    color: state.isSelected ? "#fff" : "#000",
    fontSize: "0.87rem",
    cursor: "pointer",
    "&:hover": { color: "#34A7F0" },
  }),
});

const inputSelectStyles = (lang: LangE) => ({
  control: (provided: object) => ({
    ...provided,
    height: 48,
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    fontSize: 14,
    paddingRight: 40,
  }),
  menu: (provided: object) => ({
    ...provided,
    zIndex: MENU_Z_INDEX,
  }),
  menuPortal: (provided: object) => ({
    ...provided,
    zIndex: MENU_Z_INDEX,
  }),
  option: (provided: object) => ({
    ...provided,
    fontSize: 14,
    direction: lang === LangE.HE ? "rtl" : "ltr",
  }),
  valueContainer: (provided: object) => ({
    ...provided,
    paddingInlineStart: 20,
  }),
});

const FloatingSearchForm = ({
  isForCampaignsPage = false,
  refetch,
  withoutRedirect,
  onSearchComplete,
}: FloatingSearchFormProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const lang = useAppSelector(selectCurrentLang);
  const { searchParams, addQueryParam } = useQueryParams();

  const { categories, selectedCategory, selectCategory } =
    useCategory(isForCampaignsPage);
  // Flipped by the first real focus or click anywhere in this form (capture phase). This form always
  // sets a category on mount — from the URL, from localStorage, or falling back to categories[0] —
  // so without this gate every tablet/mobile page load would download the full advertiser, brand,
  // client and industry tables (~1.68 MB) for a dropdown the user has not touched. Spec 34 D1.
  const [isActive, setIsActive] = useState(false);
  const activate = useCallback(() => setIsActive(true), []);
  const [
    autocompleteOptions,
    setAutocompleteCategory,
    selectedAutocomplete,
    setSelectedAutocomplete,
  ] = useAutocomplete(isActive);

  const [inputValue, setInputValue] = useState("");
  const [category, setCategory] = useState<SelectOptionT>(null);

  useEffect(() => {
    if (category) return;
    const categoryQueryParam = searchParams.get(FILTER_TYPE_QUERY_PARAM_NAME);
    const lastValue = getLastValue(isForCampaignsPage);
    if (categoryQueryParam && categories?.length) {
      const found = categories.find((c) => c.value === categoryQueryParam);
      if (found) setCategory(found);
    } else if (lastValue && categories?.length) {
      const found = categories.find((c) => c.value === lastValue);
      if (found) setCategory(found);
    } else if (categories?.length) {
      setCategory(categories[0]);
    }
  }, [categories, category, searchParams, isForCampaignsPage]);

  useEffect(() => {
    if (category) setAutocompleteCategory(category);
  }, [category?.value, setAutocompleteCategory]);

  useEffect(() => {
    const filterLabel = searchParams.get(FILTER_LABEL_QUERY_PARAM_NAME);
    if (filterLabel) {
      const value = filterLabel.split(":")[1]?.trim();
      if (value) setInputValue(value);
    }
  }, []);

  const handleCategoryChange = (
    selected: SelectOptionT,
    meta: ActionMeta<SelectOptionT>,
  ) => {
    if (meta.action === "select-option") {
      setCategory(selected);
      selectCategory(selected, meta);
      setSelectedAutocomplete(null);
      setInputValue("");
    }
  };

  const handleInputSelect = (
    newValue: SelectOptionT,
    meta: ActionMeta<SelectOptionT>,
  ) => {
    if (meta.action === "select-option") {
      setInputValue("");
      setSelectedAutocomplete(newValue);
    }
  };

  const handleInputChange = (value: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") setInputValue(value);
  };

  const handleSearch = useCallback(() => {
    const prevFilterLabel = searchParams.get(FILTER_LABEL_QUERY_PARAM_NAME);
    const prevFilterId = searchParams.get(FILTER_ID_QUERY_PARAM_NAME);

    const selectedOption = selectedAutocomplete;
    const selectedCategoryValue = category?.value;
    const selectedCategoryLabel = category?.label;
    const searchValue = selectedOption?.label || inputValue;

    const filterLabel = `${t(selectedCategoryLabel)}: ${searchValue}`;
    const filterId =
      (filterLabel === prevFilterLabel
        ? +prevFilterId
        : +selectedOption?.value) || null;
    const filter =
      selectedCategoryValue === "productionlocation"
        ? selectedOption?.label === "Local"
          ? "Israel"
          : "Abroad"
        : filterId
          ? ""
          : +selectedOption?.label || inputValue;
    const filterType = selectedCategoryValue;
    const sorting = searchParams.get("sorting");

    setLastValue(filterType, isForCampaignsPage);

    if (filterLabel) {
      if (withoutRedirect) {
        addQueryParam({
          filterType,
          filter,
          filterId,
          filterLabel,
        });
      } else {
        navigate(
          `/searchresults?sorting=${sorting}&filterType=${filterType}&filter=${filter}&filterId=${filterId}&filterLabel=${filterLabel}`,
        );
      }
      refetch?.();
      onSearchComplete?.();
    }
  }, [
    category,
    selectedAutocomplete,
    inputValue,
    searchParams,
    withoutRedirect,
    isForCampaignsPage,
    refetch,
    onSearchComplete,
    addQueryParam,
    navigate,
    t,
  ]);

  const handleClear = () => {
    setInputValue("");
    setSelectedAutocomplete(null);
  };

  const showClear = !!inputValue || !!selectedAutocomplete?.label;
  const options =
    category?.value === "productionlocation"
      ? productionLocationOptions
      : autocompleteOptions;
  const showMenu =
    (options?.length && inputValue) || category?.value === "productionlocation";

  return (
    <div
      className={styles.form}
      onFocusCapture={activate}
      onPointerDownCapture={activate}
    >
      <div className={styles.inputRow}>
        <div className={styles.inputWrapper}>
          <Select
            options={options}
            value={selectedAutocomplete}
            onChange={handleInputSelect}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            placeholder={
              isForCampaignsPage
                ? t`Enter request to search for campaigns`
                : t`Enter request to search for deliveries`
            }
            isRtl={lang === LangE.HE}
            components={{ MenuList, DropdownIndicator: null }}
            noOptionsMessage={() => null}
            menuIsOpen={showMenu ? undefined : false}
            controlShouldRenderValue
            styles={inputSelectStyles(lang) as any}
            menuPortalTarget={document.body}
          />
          {showClear && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear"
            >
              <Icon name="close" color={IconColorE.GRAY} />
            </button>
          )}
        </div>
        <button
          type="button"
          className={styles.searchButton}
          onClick={handleSearch}
          aria-label="Search"
        >
          <Icon name="search" color={IconColorE.WHITE} />
        </button>
      </div>
      <div className={styles.categoryRow}>
        <Select
          options={categories}
          value={category}
          onChange={handleCategoryChange}
          getOptionLabel={({ label }) => t(label)}
          styles={categorySelectStyles(lang) as any}
          isSearchable={false}
          isRtl={lang === LangE.HE}
          components={{ DropdownIndicator }}
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
};

export default FloatingSearchForm;
